export interface AssistantEvents {
  onSources?: (sources: { name: string; type: string }[]) => void
  onText?: (chunk: string) => void
  onError?: (message: string) => void
}

/**
 * Reads the assistant's SSE stream.
 *
 * EventSource only does GET, and the question has to be POSTed, so the stream
 * is parsed by hand out of the fetch body.
 */
export async function streamAssistant(
  campaignId: string,
  messages: { role: 'user' | 'assistant'; content: string }[],
  events: AssistantEvents,
  signal?: AbortSignal,
): Promise<void> {
  const response = await fetch(`/api/campaigns/${campaignId}/assistant`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ messages }),
    signal,
  })

  if (!response.ok) {
    const body = await response.json().catch(() => null) as { error?: string } | null
    events.onError?.(body?.error ?? `The assistant returned ${response.status}.`)
    return
  }
  if (!response.body) {
    events.onError?.('The assistant sent no response.')
    return
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })

    // SSE frames are separated by a blank line.
    const frames = buffer.split('\n\n')
    buffer = frames.pop() ?? ''

    for (const frame of frames) {
      let event = 'message'
      const dataLines: string[] = []
      for (const line of frame.split('\n')) {
        if (line.startsWith('event:')) event = line.slice(6).trim()
        else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim())
      }
      if (dataLines.length === 0) continue

      const raw = dataLines.join('\n')
      if (event === 'done') return

      let parsed: unknown
      try {
        parsed = JSON.parse(raw)
      } catch {
        continue // a partial frame; the next read completes it
      }

      if (event === 'text' && typeof parsed === 'string') events.onText?.(parsed)
      else if (event === 'sources' && Array.isArray(parsed)) events.onSources?.(parsed)
      else if (event === 'error' && typeof parsed === 'string') events.onError?.(parsed)
    }
  }
}
